/**
 * Platform-agnostic chat domain types. Shared by web and native UIs — this
 * module is the contract between the chat UI and Stream. No RN/DOM imports
 * (the two below are `import type` only — erased at compile, no runtime dep).
 */
import type { AgapeHighlight } from '../../components/chat/HighlightedText';
import type { RecommendationCard } from '../../types/rag';

/** A reaction summary on a message (emoji + how many people reacted). */
export interface ThreadReaction {
  /** CORE-1685 — слаг, как его отдал сервер (`agape_N` или прежний). */
  type: string;
  /** Запасной символ; интерфейс рисует картинку комплекта по `type`. */
  emoji: string;
  count: number;
}

/** CORE-1683 — the quoted original of a reply, as the server snapshotted it. */
export interface ReplyPreview {
  id: string;
  senderId?: string;
  senderName: string;
  /** CORE-1791 — автор оригинала — тот, кто смотрит: в цитате раздела сообщений — «Вы». */
  isMine?: boolean;
  text: string;
  /** Raw attachment descriptors of the original (same shape as `attachments`). */
  attachments: any[];
  /** The original was deleted after the reply was sent. */
  deleted: boolean;
  /**
   * CORE-1749 — the phrase from the original this reply is about (the person
   * selected it); absent when the whole message is answered. A snapshot like
   * `text`, and blanked with it when the original is deleted.
   */
  fragment?: string;
}

/** A single message, mapped from Stream into a render-ready shape. */
export interface ThreadMessage {
  id: string;
  text: string;
  isMine: boolean;
  createdAt: Date;
  /** Outgoing message has been read by the other side (drives ✓✓). */
  read: boolean;
  senderName: string;
  senderAvatar: string | null;
  /** CORE-642 — 192 px preview of `senderAvatar`; null when the author has none. */
  senderAvatarThumb?: string | null;
  /** Sender's backend user id — drives group-chat moderation (block user). */
  senderId?: string;
  /** Raw Stream attachments (practice cards, images, …) — rendered by the UI. */
  attachments: any[];
  reactions: ThreadReaction[];
  /**
   * Слаги реакций, которые поставил на это сообщение сам зритель. Обычно одна
   * (одна реакция от человека, см. `myReactionCalls`), но сервер хранит их по
   * типу, и прежние сборки могли оставить две — например, `love` рядом с
   * новой. CORE-1479 (возврат QA 23.10): одна строка вместо списка теряла
   * вторую свою реакцию, и её чип не снимался.
   */
  myReactions: string[];
  /** Pinned in the channel. */
  pinned: boolean;
  /**
   * CORE-1791 (возврат QA 28.10) — ключ повтора, с которым отправлено своё
   * сообщение. По нему композер узнаёт отправку, которая не дождалась ответа,
   * но дошла, и убирает подсказку «не отправлено» и вернувшийся в поле текст.
   */
  clientMessageId?: string;
  /**
   * The message this one replies to (quoted), if any. On ChatApi this is the
   * `parentMessageId` — which doubles as discussion membership (CORE-1654), so
   * inside a discussion it is the card, not a quote. See `replyTo`.
   */
  quoted: {
    id: string;
    text: string;
    senderName: string;
    /** CORE-1791 — автор цитируемого, когда он известен (поле сервера или то же сообщение в ленте). */
    senderId?: string;
    /** CORE-1791 — цитата своего сообщения: в разделе сообщений подписывается «Вы». */
    isMine?: boolean;
  } | null;
  /**
   * CORE-1683 — the specific message this one answers (`replyToMessageId`),
   * independent of the discussion it lives in. A snapshot taken at send time:
   * editing the original leaves it as is, deleting it sets `deleted` and
   * blanks text and attachments. Absent on backends without the field.
   */
  replyTo?: ReplyPreview | null;
  /** AI-generated message. */
  aiGenerated: boolean;
  /** Quick-reply options the AI attached (tappable suggested responses). */
  quickReplies: string[];
  /**
   * AI inline highlights (`agape_highlights`) — tappable phrases inside the
   * body that send a canned reply. XOR with `quickReplies` (backend enforces).
   */
  highlights?: AgapeHighlight[];
  /** AI recommendation cards (`agape_cards`) rendered below the message. */
  cards?: RecommendationCard[];
  /**
   * CORE-1751 — Агапелла предлагает записать рефлексию по Live-сессии
   * (`reflection_offer`). Кнопка под сообщением открывает то же окно, что
   * после практики, привязанное к этому завершению.
   */
  reflectionOffer?: ReflectionOfferAction;
  /**
   * CORE-1781 — бесплатные сообщения ИИ кончились (CORE-1771): Агапелла
   * ответила предложением подписки (`agapella_subscription_required`). Под
   * репликой — кнопка окна «Входит в Agapēlla».
   */
  subscriptionRequired?: boolean;
  /**
   * The original backend message object (Stream message / ChatApi dto). Carried
   * so backend-specific renderers (e.g. circle event cards, CORE-404) that need
   * the raw shape can reach it via a `renderSpecial` hook without the agnostic
   * thread knowing about them. Optional — most UIs never touch it.
   */
  raw?: any;
}

/** CORE-1751 — действие «записать рефлексию» под сообщением Агапеллы. */
export interface ReflectionOfferAction {
  /** Завершение Live-сессии, к которому привяжется запись. */
  completionId: number;
  /** Название сессии; `null` — у комнаты его нет. */
  title: string | null;
}

/** Channel-derived summary for a conversation row in the list. */
export interface ConversationMeta {
  lastMessage: string;
  /**
   * CORE-1791 — превью по частям для веб-строки (`lastMessage` — готовая русская
   * строка с «Вы: », её по-прежнему читает нативный список). Нет у источников,
   * которые его не знают.
   */
  preview?: { text: string; attachmentType: string | null; mine: boolean };
  lastMessageAt: Date | null;
  unread: number;
  // The other 1:1 member — lets the list surface conversations from people who
  // aren't in the contact list (otherwise their row never appears).
  otherUserId?: string;
  otherName?: string;
  otherImage?: string | null;
  /** CORE-642 — 192 px preview of `otherImage`; null when the peer has none. */
  otherImageThumb?: string | null;
  otherOnline?: boolean;
}

/**
 * Everything the layer needs to address a contact's channel. `entityType`
 * drives the routing: `wisdom-council` → group channel, `ai` → AI-companion
 * DM (carries the webhook flag), anything else with a `streamUserId` → plain DM.
 */
export interface ChatTarget {
  entityType: string;
  entityId: string;
  streamUserId: string;
}
